import type { LevelMeta } from './levels'
import type { BiomeId } from '@/styles/theme'

export type CornerCase = 'single' | 'edge' | 'diagonal' | 'inlet' | 'full'
export type WorldcraftMotifKind = 'start-ripple' | 'home-grove' | 'hazard-basin' | 'treasure-pocket' | 'gatework' | 'shade-nest'

export interface WorldcraftTerrainPiece {
  key: string
  x: number
  z: number
  mask: number
  cornerCase: CornerCase
  variant: number
  tone: number
  lift: number
  rot: number
  scaleX: number
  scaleZ: number
  jitterX: number
  jitterZ: number
}

export interface WorldcraftMotif {
  key: string
  kind: WorldcraftMotifKind
  x: number
  z: number
  rot: number
  scale: number
  tone: number
  tile: string
}

export interface WorldcraftLayout {
  terrain: WorldcraftTerrainPiece[]
  motifs: WorldcraftMotif[]
}

export interface MapCraftNode {
  levelIndex: number
  order: number
  x: number
  y: number
  mask: number
  variant: number
  stars: number
  completed: boolean
  unlocked: boolean
}

export interface MapCraftBridge {
  key: string
  from: MapCraftNode
  to: MapCraftNode
  variant: number
  unlocked: boolean
}

export interface BiomeMapCraft {
  nodes: MapCraftNode[]
  bridges: MapCraftBridge[]
  chips: { key: string; x: number; y: number; mask: number; variant: number; tone: number }[]
}

const FLOOR_TILES = new Set(['-', 'o', 'e', 'p', 'g', 'k', 't', 'w', 's', 'l', '~', 'm'])
const HAZARD_TILES = new Set(['l', '~', 'm'])
const TREASURE_TILES = new Set(['p', 'g', 'k'])
const GATE_TILES = new Set(['d', 'w'])

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function seeded(seed: number) {
  let s = seed >>> 0 || 1
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 0xffffffff)
}

function tileAt(grid: string[], x: number, y: number): string {
  if (y < 0 || y >= grid.length) return ' '
  if (x < 0 || x >= grid[y].length) return ' '
  return grid[y][x] ?? ' '
}

function isGroundTile(tile: string): boolean {
  return FLOOR_TILES.has(tile)
}

export function cornerMaskFor(grid: string[], x: number, y: number): number {
  const nw = isGroundTile(tileAt(grid, x - 1, y - 1)) ? 1 : 0
  const ne = isGroundTile(tileAt(grid, x, y - 1)) ? 2 : 0
  const se = isGroundTile(tileAt(grid, x, y)) ? 4 : 0
  const sw = isGroundTile(tileAt(grid, x - 1, y)) ? 8 : 0
  return nw | ne | se | sw
}

export function cornerCaseFor(mask: number): CornerCase | null {
  const bits = bitCount(mask)
  if (bits === 0) return null
  if (bits === 4) return 'full'
  if (bits === 1) return 'single'
  if (bits === 3) return 'inlet'
  if (mask === 5 || mask === 10) return 'diagonal'
  return 'edge'
}

export function bitCount(mask: number): number {
  let n = mask & 15
  let count = 0
  while (n) {
    count += n & 1
    n >>= 1
  }
  return count
}

export function buildWorldcraft(
  grid: string[],
  biome: BiomeId,
  layout: { originX: number; originZ: number; tileSize: number; gap: number },
): WorldcraftLayout {
  const rows = grid.length
  const cols = Math.max(...grid.map((r) => r.length))
  const pitch = layout.tileSize + layout.gap
  const seed = hashString(`${biome}:${grid.join('/')}`)
  const terrain: WorldcraftTerrainPiece[] = []

  for (let y = 0; y <= rows; y++) {
    for (let x = 0; x <= cols; x++) {
      const mask = cornerMaskFor(grid, x, y)
      const cornerCase = cornerCaseFor(mask)
      if (!cornerCase) continue
      const rnd = seeded(seed ^ Math.imul(x + 17, 73856093) ^ Math.imul(y + 31, 19349663) ^ Math.imul(mask, 83492791))
      const wx = layout.originX + (x - 0.5) * pitch
      const wz = layout.originZ + (y - 0.5) * pitch
      terrain.push({
        key: `${x}:${y}:${mask}`,
        x: wx,
        z: wz,
        mask,
        cornerCase,
        variant: Math.floor(rnd() * 4),
        tone: rnd(),
        lift: (rnd() - 0.5) * 0.035,
        rot: Math.floor(rnd() * 4) * (Math.PI / 2),
        scaleX: 0.96 + rnd() * 0.12,
        scaleZ: 0.96 + rnd() * 0.12,
        jitterX: (rnd() - 0.5) * 0.045,
        jitterZ: (rnd() - 0.5) * 0.045,
      })
    }
  }

  return { terrain, motifs: buildMotifs(grid, layout, seed) }
}

function buildMotifs(
  grid: string[],
  layout: { originX: number; originZ: number; tileSize: number; gap: number },
  seed: number,
): WorldcraftMotif[] {
  const pitch = layout.tileSize + layout.gap
  const motifs: WorldcraftMotif[] = []
  const seenKinds = new Map<WorldcraftMotifKind, number>()
  const add = (kind: WorldcraftMotifKind, tile: string, x: number, y: number) => {
    const count = seenKinds.get(kind) ?? 0
    const limit = kind === 'hazard-basin' || kind === 'treasure-pocket' ? 5 : kind === 'shade-nest' ? 4 : 2
    if (count >= limit) return
    seenKinds.set(kind, count + 1)
    const rnd = seeded(seed ^ Math.imul(x + 41, 2654435761) ^ Math.imul(y + 59, 2246822519) ^ hashString(kind))
    motifs.push({
      key: `${kind}:${x}:${y}:${count}`,
      kind,
      tile,
      x: layout.originX + x * pitch,
      z: layout.originZ + y * pitch,
      rot: rnd() * Math.PI * 2,
      scale: 0.78 + rnd() * 0.48,
      tone: rnd(),
    })
  }

  for (let y = 0; y < grid.length; y++) {
    for (let x = 0; x < grid[y].length; x++) {
      const tile = tileAt(grid, x, y)
      if (tile === 'o') add('start-ripple', tile, x, y)
      else if (tile === 'e') add('home-grove', tile, x, y)
      else if (tile === 'm') add('shade-nest', tile, x, y)
      else if (HAZARD_TILES.has(tile)) add('hazard-basin', tile, x, y)
      else if (TREASURE_TILES.has(tile)) add('treasure-pocket', tile, x, y)
      else if (GATE_TILES.has(tile)) add('gatework', tile, x, y)
    }
  }

  return motifs
}

export function buildBiomeMapCraft(
  levels: Pick<LevelMeta, 'index' | 'grid'>[],
  progress: { completedLevels: number[]; levelStars: Record<number, number> },
  unlocked: boolean,
  biome: BiomeId,
): BiomeMapCraft {
  const seed = hashString(`${biome}:${levels.map((l) => `${l.index}:${l.grid.join('/')}`).join('|')}`)
  const count = Math.max(1, levels.length)
  const completed = new Set(progress.completedLevels)
  const dense = count > 16
  const denseCols = dense ? Math.ceil(Math.sqrt(count * 1.45)) : 0
  const denseRows = dense ? Math.ceil(count / denseCols) : 0
  const nodes: MapCraftNode[] = levels.map((level, order) => {
    const rnd = seeded(seed ^ Math.imul(level.index + 1, 1103515245))
    const col = dense ? order % denseCols : 0
    const row = dense ? Math.floor(order / denseCols) : 0
    const serpentineCol = dense && row % 2 === 1 ? denseCols - 1 - col : col
    const x = dense
      ? Math.max(8, Math.min(92, 8 + (serpentineCol / Math.max(1, denseCols - 1)) * 84 + (rnd() - 0.5) * 4))
      : count === 1 ? 50 : 9 + (order / (count - 1)) * 82
    const wave = Math.sin(order * 1.35 + rnd() * 2.2) * (dense ? 3 : 18)
    const y = dense
      ? Math.max(16, Math.min(84, 17 + (row / Math.max(1, denseRows - 1)) * 66 + wave))
      : Math.max(18, Math.min(82, 50 + wave + (order % 2 === 0 ? -5 : 5)))
    const stars = progress.levelStars[level.index] ?? 0
    const nodeUnlocked = unlocked && (order === 0 || completed.has(levels[order - 1]?.index ?? -1))
    const mask = cornerMaskFor(level.grid, Math.max(0, Math.floor(level.grid[0].length / 2)), Math.max(0, Math.floor(level.grid.length / 2))) || (stars > 0 ? 15 : 5)
    return {
      levelIndex: level.index,
      order,
      x,
      y,
      mask,
      variant: Math.floor(rnd() * 4),
      stars,
      completed: completed.has(level.index),
      unlocked: nodeUnlocked,
    }
  })
  const bridges = nodes.slice(1).map((node, i) => ({
    key: `${nodes[i].levelIndex}:${node.levelIndex}`,
    from: nodes[i],
    to: node,
    variant: (node.variant + nodes[i].variant) % 4,
    unlocked: node.unlocked && nodes[i].unlocked,
  }))
  const chips = nodes.flatMap((node) => {
    const rnd = seeded(seed ^ Math.imul(node.levelIndex + 13, 97531))
    return [0, 1, 2].map((i) => ({
      key: `${node.levelIndex}:${i}`,
      x: Math.max(3, Math.min(97, node.x + (rnd() - 0.5) * 16)),
      y: Math.max(8, Math.min(92, node.y + (rnd() - 0.5) * 22)),
      mask: ((node.mask << i) | (node.mask >> (4 - i))) & 15,
      variant: Math.floor(rnd() * 4),
      tone: rnd(),
    })).filter((chip) => chip.mask > 0)
  })

  return { nodes, bridges, chips }
}
